import { computed, type MaybeRef, unref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useApi } from "@shopexperts/shared";

interface UseExpertWalletOptions {
  actingOrgId: MaybeRef<number | null | undefined>;
  enabled?: MaybeRef<boolean>;
}

function isPositiveId(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

export function useExpertWallet(options: UseExpertWalletOptions) {
  const api = useApi();
  const actingOrgId = computed(() => unref(options.actingOrgId));
  const enabled = computed(() => (unref(options.enabled) ?? true) && isPositiveId(actingOrgId.value));

  const query = useQuery({
    queryKey: computed(() => ["expert", "wallet", actingOrgId.value]),
    queryFn: async () => {
      if (!isPositiveId(actingOrgId.value)) {
        throw new Error("actingOrgId is required to load expert wallet");
      }

      return await api.expert.wallet.getWallet({
        query: { acting_org_id: actingOrgId.value },
      });
    },
    enabled,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });

  const wallet = computed(() => query.data.value?.wallet ?? null);

  return {
    wallet,
    availableBalanceCents: computed(() => query.data.value?.available_balance_cents ?? wallet.value?.available_balance_cents ?? 0),
    holdBalanceCents: computed(() => query.data.value?.hold_balance_cents ?? wallet.value?.hold_balance_cents ?? 0),
    totalGrossSalesCents: computed(() => query.data.value?.total_gross_sales_cents ?? wallet.value?.lifetime_gross_cents ?? 0),
    totalNetCents: computed(() => query.data.value?.total_net_cents ?? wallet.value?.lifetime_net_cents ?? 0),
    currency: computed(() => query.data.value?.currency ?? wallet.value?.currency ?? null),
    allowPayouts: computed(() => query.data.value?.allow_payouts === true),
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    query,
  };
}
